<!-- src/apps/secret/views/status/StatusView.vue -->

<script setup lang="ts">
  /**
   * Public service status page (/status).
   *
   * On-brand, dependency-free view over the PUBLIC aggregate endpoint
   * (GET /api/v2/status/metrics). That endpoint is auth=noauth and, by
   * construction, exposes only safe aggregates — overall status, per-dependency
   * health as up/down booleans, the lifetime "protected secrets" total, and the
   * trailing daily demand series. No admin/PII data is fetched or shown here.
   *
   * The demand chart REUSES the admin observability sparkline
   * (TrendSparkline) — a presentational leaf whose `{date, count}[]` point shape
   * matches our daily series 1:1; the hue comes from the wrapper's text color.
   *
   * connect-src 'self' permits the same-origin fetch.
   */
  import TrendSparkline from '@/apps/admin/components/TrendSparkline.vue';
  import {
    systemStatusMetricsResponseSchema,
    type SystemStatusMetricsResponse,
  } from '@/schemas/api/v2/responses/meta';
  import { computed, onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();

  type LoadState = 'idle' | 'loading' | 'loaded' | 'error';

  const state = ref<LoadState>('idle');
  const data = ref<SystemStatusMetricsResponse | null>(null);

  async function load() {
    if (state.value === 'loading') return;
    state.value = 'loading';
    try {
      const res = await fetch('/api/v2/status/metrics', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const parsed = systemStatusMetricsResponseSchema.safeParse(json);
      if (!parsed.success) {
        console.warn('[status] response failed validation', parsed.error);
        throw parsed.error;
      }
      data.value = parsed.data;
      state.value = 'loaded';
    } catch (err) {
      console.warn('[status] Failed to load status metrics', err);
      state.value = 'error';
    }
  }

  const isNominal = computed(() => data.value?.status === 'nominal');

  const checks = computed(() => {
    const c = data.value?.checks;
    if (!c) return [];
    return [
      { key: 'datastore', label: t('web.status.check_datastore'), up: c.datastore },
      { key: 'database', label: t('web.status.check_database'), up: c.database },
    ];
  });

  const num = (value: number | undefined | null): string =>
    typeof value === 'number' ? value.toLocaleString() : '—';

  const secretsTotal = computed(() =>
    num(data.value?.metrics.secrets_created_total)
  );

  const daily = computed(() => data.value?.metrics.secrets_created_daily ?? []);
  const days = computed(() => data.value?.metrics.days ?? 30);

  const todayCount = computed(() => {
    const series = daily.value;
    return series.length > 0 ? series[series.length - 1].count : 0;
  });

  const peakCount = computed(() =>
    daily.value.reduce((max, point) => Math.max(max, point.count), 0)
  );

  const hasDemand = computed(() => peakCount.value > 0);

  const sparklineLabel = computed(() =>
    t('web.status.demand_aria', { days: days.value, count: todayCount.value })
  );

  onMounted(load);
</script>

<template>
  <article class="mx-auto w-full max-w-4xl px-5 pb-16 sm:px-8">
    <!-- Back link -->
    <router-link
      to="/"
      class="inline-flex items-center gap-2 pt-2 font-mono text-[11px] tracking-[0.08em] text-day-ink-faint
        uppercase transition-colors hover:text-brand-600
        dark:text-night-ink-faint dark:hover:text-brand-500">
      <span aria-hidden="true">&larr;</span>
      {{ t('web.status.back') }}
    </router-link>

    <!-- Header -->
    <header class="mt-8 border-b border-day-line pb-8 dark:border-white/[0.07]">
      <p class="mb-3 font-mono text-[10px] tracking-[0.1em] text-brand-600 uppercase dark:text-brand-500">
        {{ t('web.status.eyebrow') }}
      </p>
      <h1 class="font-brand text-[34px] leading-[1.15] font-bold text-day-ink sm:text-[40px] dark:text-night-ink">
        {{ t('web.status.title') }}
      </h1>
      <p class="mt-4 max-w-2xl text-[15px] leading-[1.7] font-light text-day-ink-muted dark:text-night-ink-muted">
        {{ t('web.status.intro') }}
      </p>
    </header>

    <!-- Loading -->
    <div
      v-if="state === 'loading' || state === 'idle'"
      class="mt-10 flex items-center gap-3 font-mono text-[12px] text-day-ink-faint dark:text-night-ink-faint"
      data-testid="status-loading">
      <svg
        class="size-4 animate-spin"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true">
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="3" />
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 0 1 8-8v3a5 5 0 0 0-5 5H4z" />
      </svg>
      {{ t('web.status.loading') }}
    </div>

    <!-- Error -->
    <div
      v-else-if="state === 'error'"
      class="mt-10 border border-rose-300/60 bg-rose-50 px-5 py-4 dark:border-rose-500/30 dark:bg-rose-500/10"
      data-testid="status-error">
      <p class="font-mono text-[12px] font-semibold tracking-[0.08em] text-rose-700 uppercase dark:text-rose-300">
        {{ t('web.status.error_title') }}
      </p>
      <p class="mt-1 text-[13px] text-day-ink-muted dark:text-night-ink-muted">
        {{ t('web.status.error_body') }}
      </p>
      <button
        type="button"
        class="mt-3 inline-flex items-center border border-day-line px-3 py-1.5 font-mono text-[11px]
          tracking-[0.06em] text-day-ink uppercase transition-colors hover:bg-day-100
          dark:border-white/[0.12] dark:text-night-ink dark:hover:bg-night-100"
        @click="load">
        {{ t('web.status.retry') }}
      </button>
    </div>

    <!-- Loaded -->
    <div
      v-else-if="state === 'loaded' && data"
      class="mt-8 space-y-10"
      data-testid="status-loaded">
      <!-- 1) Service status -->
      <section data-testid="status-health">
        <h2 class="mb-4 font-mono text-[11px] font-semibold tracking-[0.12em] text-day-ink-faint uppercase dark:text-night-ink-faint">
          {{ t('web.status.health_heading') }}
        </h2>
        <div
          class="border p-5"
          :class="
            isNominal
              ? 'border-emerald-300/60 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10'
              : 'border-amber-300/60 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10'
          ">
          <div class="flex items-center gap-3">
            <span
              class="inline-block size-3 shrink-0 rounded-full"
              :class="isNominal ? 'bg-emerald-500' : 'bg-amber-500'"
              aria-hidden="true"></span>
            <span
              class="font-mono text-sm font-bold tracking-[0.08em] uppercase"
              :class="
                isNominal
                  ? 'text-emerald-700 dark:text-emerald-300'
                  : 'text-amber-700 dark:text-amber-300'
              "
              data-testid="status-indicator">
              {{ isNominal ? t('web.status.status_nominal') : t('web.status.status_degraded') }}
            </span>
          </div>
          <p class="mt-2 text-[13px] leading-relaxed text-day-ink-muted dark:text-night-ink-muted">
            {{ isNominal ? t('web.status.status_nominal_note') : t('web.status.status_degraded_note') }}
          </p>

          <!-- Per-dependency pills -->
          <dl class="mt-4 flex flex-wrap gap-3">
            <div
              v-for="check in checks"
              :key="check.key"
              class="inline-flex items-center gap-2 border border-day-line bg-day px-3 py-1.5
                dark:border-white/[0.12] dark:bg-night"
              :data-testid="`status-check-${check.key}`">
              <span
                class="inline-block size-2 shrink-0 rounded-full"
                :class="check.up ? 'bg-emerald-500' : 'bg-rose-500'"
                aria-hidden="true"></span>
              <dt class="font-mono text-[11px] tracking-[0.06em] text-day-ink uppercase dark:text-night-ink">
                {{ check.label }}
              </dt>
              <dd
                class="font-mono text-[10px] tracking-[0.08em] uppercase"
                :class="
                  check.up
                    ? 'text-emerald-700 dark:text-emerald-400'
                    : 'text-rose-700 dark:text-rose-400'
                ">
                {{ check.up ? t('web.status.check_up') : t('web.status.check_down') }}
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <!-- 2) Protected secrets (hero number) -->
      <section data-testid="status-secrets">
        <h2 class="mb-4 font-mono text-[11px] font-semibold tracking-[0.12em] text-day-ink-faint uppercase dark:text-night-ink-faint">
          {{ t('web.status.secrets_heading') }}
        </h2>
        <div class="border border-day-line bg-day-100 px-5 py-6 dark:border-white/[0.07] dark:bg-night-100">
          <p
            class="font-brand text-[44px] leading-none font-bold text-day-ink sm:text-[56px] dark:text-night-ink"
            data-testid="status-secrets-total">
            {{ secretsTotal }}
          </p>
          <p class="mt-3 text-[13px] leading-relaxed text-day-ink-muted dark:text-night-ink-muted">
            {{ t('web.status.secrets_note') }}
          </p>
        </div>
      </section>

      <!-- 3) Demand chart (reuses the admin observability sparkline) -->
      <section data-testid="status-demand">
        <h2 class="mb-4 font-mono text-[11px] font-semibold tracking-[0.12em] text-day-ink-faint uppercase dark:text-night-ink-faint">
          {{ t('web.status.demand_heading') }}
        </h2>
        <div class="border border-day-line bg-day-100 p-5 dark:border-white/[0.07] dark:bg-night-100">
          <div class="flex flex-wrap items-baseline justify-between gap-2">
            <p class="font-mono text-[11px] tracking-[0.08em] text-day-ink-faint uppercase dark:text-night-ink-faint">
              {{ t('web.status.demand_window', { days }) }}
            </p>
            <p class="font-mono text-[11px] text-day-ink-muted dark:text-night-ink-muted">
              {{ t('web.status.demand_peak', { count: num(peakCount) }) }}
            </p>
          </div>

          <div
            v-if="hasDemand"
            class="mt-4 overflow-x-auto text-brand-600 dark:text-brand-500">
            <div class="min-w-[320px]">
              <TrendSparkline
                :points="daily"
                :label="sparklineLabel"
                testid="status-demand-sparkline" />
            </div>
          </div>
          <p
            v-else
            class="mt-4 font-mono text-[12px] text-day-ink-faint dark:text-night-ink-faint"
            data-testid="status-demand-empty">
            {{ t('web.status.demand_empty') }}
          </p>

          <p class="mt-4 text-[12px] leading-relaxed text-day-ink-muted dark:text-night-ink-muted">
            {{ t('web.status.demand_note') }}
          </p>
        </div>
      </section>

      <!-- Footer meta: public version -->
      <p
        class="border-t border-day-line pt-6 font-mono text-[11px] text-day-ink-faint dark:border-white/[0.07] dark:text-night-ink-faint"
        data-testid="status-version">
        {{ t('web.status.version_label', { version: data.version }) }}
      </p>
    </div>
  </article>
</template>
